import { Injectable } from '@angular/core';
import { IClient } from '../model/client.model';
import { IDocumentCheckbox, IDocumentHandSignField } from '../model/document-field.model';
import { IDocumentSigner } from '../model/document-signer.model';
import { TxDocument } from '../model/document.model';
import {
  CsasDtoCommonDocumentState,
  CsasDtoConfirmDocumentState,
  CsasDtoSignDocumentState,
} from '../model/dtos/csas/document-state.model';
import {
  ICsasDtoClient,
  ICsasDtoDocument,
  ICsasDtoDocumentCheckbox,
  ICsasDtoDocumentSignField,
  ICsasDtoTransaction,
  ICsasDtoTransactionSection,
} from '../model/dtos/csas/dto.model';
import { CsasDtoTransactionState } from '../model/dtos/csas/transaction-state.model';
import { SigningState } from '../model/signing-state.model';
import { TransactionSection } from '../model/transaction-section.model';
import { Transaction } from '../model/transaction.model';
import { DtoParserService } from './dto-parser.service';
import { v4 as uuidv4 } from 'uuid';
import { ClientTxState } from '../model/client-state.model';
import { CsasDtoDocumentSignFieldState } from '../model/dtos/csas/document-field-state.model';
import { SignatureType } from '../model/signature-type.model';
import { CSAS_DTO_SESSION_CLIENT } from '../model/dtos/csas/session-client.model';
import { SigningRequestChannel } from '../model/signing-request-channel.model';
import { PointerType } from 'ngx-mat-esign360/lib/shared/model/pointer-type.model';

@Injectable()
export class CsasDtoParserService extends DtoParserService {
  constructor() {
    super();
  }

  parseTransaction(dtoTransaction: ICsasDtoTransaction): Transaction {
    return new Transaction(
      dtoTransaction.transactionId,
      dtoTransaction.externalId,
      dtoTransaction.correlationId,
      dtoTransaction.title,
      SigningState.NA,
      dtoTransaction.state === CsasDtoTransactionState.CollectingSignatures ||
        dtoTransaction.state === CsasDtoTransactionState.BankerReviewing,
      new Date(),
      null,
      this.parseClients(dtoTransaction.clients).concat(
        this.createSessionClient(dtoTransaction.sections)
      ),
      this.parseTransactionSections(dtoTransaction.sections, dtoTransaction.clients)
    );
  }

  parseClients(dtoClients: ICsasDtoClient[]): IClient[] {
    return dtoClients.map((client) => {
      const { cluid, ...clientInfo } = client;
      return {
        ...clientInfo,
        id: client.cluid,
        personalId: '',
        phone: '',
        birthYear: '',
        stateData: null,
        prerequisites: [],
        signingRequestChannel: SigningRequestChannel.None,
        preferedLang: 'cs',
      };
    });
  }

  parseTransactionSections(
    dtoTransactionSections: ICsasDtoTransactionSection[],
    dtoClients: ICsasDtoClient[]
  ): TransactionSection[] {
    return dtoTransactionSections.map(
      (section) =>
        new TransactionSection(
          section.sectionId,
          this.parseDocuments(section.documents, dtoClients)
        )
    );
  }

  parseDocuments(dtoDocuments: ICsasDtoDocument[], dtoClients: ICsasDtoClient[]): TxDocument[] {
    return dtoDocuments.map(
      (document) =>
        new TxDocument(
          document.documentId,
          document.documentName,
          document.documentFileId,
          document.actionType,
          document.documentMetadata,
          document.state === CsasDtoCommonDocumentState.DocumentDisabled ||
            document.state === CsasDtoCommonDocumentState.PreparingDocument,
          document.state === CsasDtoCommonDocumentState.DocumentProcessed ||
            document.state === CsasDtoCommonDocumentState.ProcessedDocumentRead,
          false,
          document.state === CsasDtoSignDocumentState.InsertingSignature,
          document.state === CsasDtoSignDocumentState.Finalized,
          this.parseSigners(document, dtoClients).concat(this.createSessionSigner(document))
        )
    );
  }

  parseSigners(dtoDocument: ICsasDtoDocument, dtoClients: ICsasDtoClient[]): IDocumentSigner[] {
    return dtoClients.map((client) => ({
      clientId: client.cluid,
      relation: dtoDocument.actionType,
      signingState: SigningState.NA,
      signFields: this.parseSignFields(dtoDocument.signatures),
      checkboxes: this.parseCheckboxes(dtoDocument.formFields),
    }));
  }

  parseCheckboxes(dtoCheckboxes: ICsasDtoDocumentCheckbox[]): IDocumentCheckbox[] {
    return dtoCheckboxes.map((checkbox) => ({
      id: checkbox.formFieldId,
      checked: checkbox.formFieldValue === 'checked',
      required: checkbox.required,
      visible: checkbox.visible,
      pdfId: checkbox.formFieldPdfId,
    }));
  }

  parseSignFields(dtoSignFields: ICsasDtoDocumentSignField[]): IDocumentHandSignField[] {
    return dtoSignFields.map((signField) => ({
      id: signField.signatureId,
      state: signField.state,
      pdfId: signField.signFieldPdfId,
      clientUid: signField.cluid,
      type: SignatureType.Hand,
      allowedHandDeviceTypes: [PointerType.Mouse, PointerType.Pen, PointerType.Touch],
      disabled:
        signField.state === CsasDtoDocumentSignFieldState.SignatureStart ||
        signField.state === CsasDtoDocumentSignFieldState.SignatureDisabled ||
        signField.state === CsasDtoDocumentSignFieldState.Signed,
      signed: signField.state === CsasDtoDocumentSignFieldState.Signed,
    }));
  }

  createSessionClient(dtoTransactionSections: ICsasDtoTransactionSection[]): IClient {
    let openedDocument = null;
    for (const section of dtoTransactionSections) {
      openedDocument = section.documents.find(
        (document) =>
          document.state === CsasDtoSignDocumentState.DocumentEdit ||
          document.state === CsasDtoCommonDocumentState.ProcessedDocumentRead ||
          document.state === CsasDtoConfirmDocumentState.PreparedDocumentRead
      );
      if (openedDocument) {
        break;
      }
    }
    const activeSignField = openedDocument?.signatures.find(
      (signField) => signField.state === CsasDtoDocumentSignFieldState.Signing
    );

    return {
      id: CSAS_DTO_SESSION_CLIENT.cluid,
      personalId: uuidv4(),
      forename: 'Tx',
      surname: 'Runner',
      email: 'tx.runner@example.com',
      phone: '',
      birthYear: '',
      stateData: {
        state: activeSignField?.signatureId
          ? ClientTxState.Signing
          : openedDocument?.documentId
          ? ClientTxState.DocumentOpen
          : ClientTxState.Summary,
        signingState: SigningState.NA,
        documentId: openedDocument.documentId,
        signatureId: null,
        prerequisiteStates: {
          inProcess: null,
          required: [],
          fulfilled: [],
        },
      },
      prerequisites: [],
      signingRequestChannel: SigningRequestChannel.None,
    };
  }

  createSessionSigner(dtoDocument: ICsasDtoDocument): IDocumentSigner {
    return {
      clientId: CSAS_DTO_SESSION_CLIENT.cluid,
      relation: dtoDocument.actionType,
      signingState: SigningState.NA,
      signFields: this.parseSignFields(dtoDocument.signatures),
      checkboxes: this.parseCheckboxes(dtoDocument.formFields),
    };
  }
}
